<template>
  <ApproverDashboardHeader />
  <KeepAlive>
    <component :is="tableComponent" :accounts>
      <template #header="{ updateSearch, search }">
        <WdsStack direction-column gap-sm class="tw-mt-8">
          <FeatureFlagged name="cross-account-requests">
            <WdsStack v-if="haveMultipleAccounts" gap-sm align-center>
              <WdsToggle v-model:checked="allAccounts">
                <WdsStack align-center gap-xs>
                  {{ $t('See approvals across accounts') }}
                  <WdsTooltip
                    :text="
                      $t(
                        'See results from all accounts that you have access to. This requires that you are part of a team on the accounts that also have approvals enabled. This is still in beta so some approvals on certain accounts might not show properly.',
                      )
                    "
                    global
                  >
                    <WdsIcon name="help-filled" :size="16" class="tw-text-gray-600" />
                  </WdsTooltip>
                  <WdsLabel xs warning subtle>
                    Beta
                    <WdsIcon
                      :size="13"
                      weight="thin"
                      :name="WdsIconNameEnum.LIGHT"
                      class="!tw-inline-block"
                    />
                  </WdsLabel>
                </WdsStack>
              </WdsToggle>
            </WdsStack>
          </FeatureFlagged>
          <WdsStack class="tw-items-center" justify-space-between>
            <WdsStack justify-center align-center gap-md>
              <ApproverDashboardFilters :model-value="search" @update:model-value="updateSearch" />
            </WdsStack>
            <WdsStack v-if="search">
              <WdsText secondary class="search-text">
                {{ $t('Showing results for “:search”', { search }) }}
              </WdsText>
              <WdsButtonGhost class="tw-text-lg" secondary @click.prevent="updateSearch('')">
                {{ $t('Clear search') }}
              </WdsButtonGhost>
            </WdsStack>
            <WdsStack v-else class="tw-items-center" gap-md>
              <WdsText>
                {{ $t('View') }}
              </WdsText>
              <WdsSegmentedControl
                :default-active-key="reviewStatus"
                :options="requestStatusOptions"
                @click="reviewStatus = $event"
              />
            </WdsStack>
          </WdsStack>
        </WdsStack>
      </template>
    </component>
  </KeepAlive>
</template>

<script setup lang="ts">
import { computed, ComputedRef, ref } from 'vue'
import {
  WdsButtonGhost,
  WdsIcon,
  WdsIconNameEnum,
  WdsLabel,
  WdsSegmentedControl,
  WdsStack,
  WdsText,
  WdsToggle,
  WdsTooltip,
} from '@worksome/wds-components'
import { useQuery } from '@vue/apollo-composable'
import ApproverDashboardHeader from '@/components/Workflows/Approvals/Pages/ApproverDashboard/ApproverDashboardHeader.vue'
import TablePendingApprovals from '@/components/Workflows/Approvals/Pages/ApproverDashboard/TablePendingApprovals.vue'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import TableReviewedApprovals from '@/components/Workflows/Approvals/Pages/ApproverDashboard/TableReviewedApprovals.vue'
import ApproverDashboardFilters from '@/components/Workflows/Approvals/Pages/ApproverDashboard/ApproverDashboardFilters.vue'
import { graphql } from '@/gql'
import FeatureFlagged from '@/components/FeatureFlagged.vue'

const clientId = 'publicApiCustomFields'

const reviewStatus = ref('pending')
const allAccounts = ref(false)

const { result, loading } = useQuery(
  graphql(`
    query Accounts {
      accounts {
        id
      }
    }
  `),
  null,
  { clientId },
)

const accounts: ComputedRef<string[]> = computed(() => {
  const accountsRes = result.value?.accounts
  if (allAccounts.value && !loading.value && accountsRes && accountsRes.length > 0) {
    return accountsRes.map((acc) => acc.id)
  }
  return [currentAccount.globalId]
})

const haveMultipleAccounts: ComputedRef<boolean> = computed(() => {
  const accountsRes = result.value?.accounts
  if (!loading.value && accountsRes) {
    return accountsRes.length > 1
  }
  return false
})

const requestStatusOptions = [
  {
    optionKey: 'pending',
    title: $t('Pending'),
  },
  {
    optionKey: 'reviewed',
    title: $t('Reviewed'),
  },
]

const tableComponent = computed(() => {
  switch (reviewStatus.value) {
    case 'pending':
      return TablePendingApprovals
    case 'reviewed':
      return TableReviewedApprovals
    default:
      return TablePendingApprovals
  }
})
</script>

<style lang="scss" scoped>

.search-text {
  &:deep(.wds-text-secondary) {
    @apply tw-max-w-72;
    @apply tw-text-nowrap;
    @apply tw-text-ellipsis;
    @apply tw-overflow-hidden;
  }
}
</style>
